import { useState, useRef, useCallback } from "react";
import { archiveSearchKick, archiveSearchPoll } from "../api";
import type { ArchiveSearchParams, ArchiveSearchJob } from "../types";

const POLL_INTERVAL_MS = 3000;

type UseArchiveSearchState = {
	job: ArchiveSearchJob | null;
	loading: boolean;
	error: string | null;
};

type UseArchiveSearchReturn = UseArchiveSearchState & {
	kick: (params: ArchiveSearchParams) => Promise<void>;
	reset: () => void;
};

/**
 * Hook that manages the lifecycle of an archive search job:
 * 1. POST /api/audit/admin/archive-search → receive jobId
 * 2. Poll GET /api/audit/admin/archive-search/:jobId every 3s
 * 3. Stop polling when status is 'done' or 'failed'
 */
export function useArchiveSearch(): UseArchiveSearchReturn {
	const [state, setState] = useState<UseArchiveSearchState>({
		job: null,
		loading: false,
		error: null,
	});

	const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const jobIdRef = useRef<string | null>(null);
	const abortedRef = useRef(false);

	const stopPolling = useCallback(() => {
		if (pollTimerRef.current != null) {
			clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	}, []);

	const schedulePoll = useCallback(
		(jobId: string) => {
			stopPolling();
			pollTimerRef.current = setTimeout(async () => {
				if (abortedRef.current) return;
				try {
					const job = await archiveSearchPoll(jobId);
					setState((prev) => ({ ...prev, job }));
					if (job.status === "done" || job.status === "failed") {
						setState((prev) => ({ ...prev, loading: false }));
						stopPolling();
					} else {
						schedulePoll(jobId);
					}
				} catch (err: any) {
					setState({ job: null, loading: false, error: err?.message ?? "Poll failed" });
					stopPolling();
				}
			}, POLL_INTERVAL_MS);
		},
		[stopPolling]
	);

	const kick = useCallback(
		async (params: ArchiveSearchParams) => {
			stopPolling();
			abortedRef.current = false;
			setState({ job: null, loading: true, error: null });
			try {
				const { jobId } = await archiveSearchKick(params);
				jobIdRef.current = jobId;
				// Immediately seed state with a pending job so UI shows progress right away
				setState({
					job: {
						jobId,
						status: "pending",
						progress: 0,
						totalObjects: null,
						processedObjects: 0,
						results: null,
						errorMessage: null,
					},
					loading: true,
					error: null,
				});
				schedulePoll(jobId);
			} catch (err: any) {
				setState({ job: null, loading: false, error: err?.message ?? "Failed to start search" });
			}
		},
		[schedulePoll, stopPolling]
	);

	const reset = useCallback(() => {
		abortedRef.current = true;
		stopPolling();
		jobIdRef.current = null;
		setState({ job: null, loading: false, error: null });
	}, [stopPolling]);

	return { ...state, kick, reset };
}
